'use client';
import { useState } from 'react';
import { SITE } from '~/data/site';

interface QuickLeadFormProps {
  source?: string;
  label?: string;
}

export function QuickLeadForm({ source = 'site', label = 'Get started →' }: QuickLeadFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);

  if (status === 'done') {
    return (
      <div className="qlf-success">
        <span className="qlf-success-check">✓</span>
        <span>Got it — we&rsquo;ll reach out within 10 minutes.</span>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !consent) return;
    setStatus('submitting');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim(), source }),
      });
      if (!res.ok) throw new Error('submit failed');
      setStatus('done');
    } catch {
      // Was `setStatus('idle')` — the form silently reset to blank and the
      // visitor had no idea the lead never arrived. This is the most-placed
      // form on the site (three slots on the homepage alone).
      setStatus('error');
    }
  }

  return (
    <form className="qlf" onSubmit={handleSubmit} noValidate>
      <input
        className="qlf-input"
        type="text"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        aria-label="Your name"
      />
      <input
        className="qlf-input"
        type="tel"
        placeholder="(727) 555-0100"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        required
        aria-label="Phone number"
      />
      <label className="qlf-consent">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
          aria-label="Consent to be contacted"
        />
        <span>
          I agree to receive a call or text from New Leaf Cleaning at the number above.
          Consent not required for purchase. Msg/data rates may apply. See our{' '}
          <a href="/policies#privacy" target="_blank" rel="noopener noreferrer">
            privacy policy
          </a>
          .
        </span>
      </label>
      <button
        type="submit"
        className="qlf-btn"
        disabled={status === 'submitting' || !name.trim() || !phone.trim() || !consent}
        aria-label={label}
      >
        {status === 'submitting' ? '...' : label}
      </button>
      {/* A failed submit MUST say so and give a way through — a lead that
          vanishes silently is worse than no form. `role="alert"` makes screen
          readers announce it without moving focus. */}
      {status === 'error' && (
        <p className="qlf-error" role="alert">
          That didn&rsquo;t send. Please try again, or email us at{' '}
          <a href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>.
        </p>
      )}
      <input type="hidden" name="source" value={source} />
    </form>
  );
}
